import React from 'react';
import { ErrorRenderer } from '../errorRenderer';
import { Providers, ProvidersProps } from '../../../providers';
import { IReactAppError, ReactAppError, ReactAppErrorType, ReactAppUnknownError } from '../../../../../../common/model/reactApp/error';

interface ErrorBoundaryState {
  error: IReactAppError | undefined;
}

export interface ErrorBoundaryProps extends ProvidersProps, ErrorBoundaryState {
  onRedirect?: (url: string) => void;
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: props.error };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error: error instanceof ReactAppError ? error : new ReactAppUnknownError(error) };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  componentDidCatch(error, info) {
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    // logErrorToMyService(error, info.componentStack);
  }

  render() {
    const { error } = this.state;

    if (error) {
      if (error.type === ReactAppErrorType.Redirect && this.props.onRedirect) {
        try {
          this.props.onRedirect(String(error.payload));
          this.setState({ error: undefined });
        } catch {}
      } else if (error.type === ReactAppErrorType.Unknown) {
        console.error(error.stack ?? error.message ?? 'Unknown error');
      }

      return (
        <Providers {...this.props}>
          <ErrorRenderer error={error} />
        </Providers>
      );
    }

    return this.props.children;
  }
}
